import React, { Component, Fragment, Children, cloneElement } from 'react';
import Media from 'react-media';
import { Field, validateYupSchema, getIn } from 'formik';
import { compose, branch, renderComponent, renderNothing } from 'recompose';
import { Redirect } from 'react-router';
import FormWizard from '../formwizard';
import { ActionButton } from '../buttons';
import Fieldset from '../fieldset';
import DeliveryAddress from './deliveryAddress';
import InvoiceAddress from './invoiceAddress';
import Error from './validationError';
import {
  Wrapper,
  FormContainer,
  WizardControls,
  SummaryToggle,
  Divider,
} from './style';
import Tip from '../tip';
import { StepOneSchema, StepTwoSchema } from '../../util/schemas';
import { mapFormValuesToOrder } from '../../util';

// ---
// Form Container.
// ---

const CheckoutStep = ({ children, toggleSummary, ...rest }) => {
  const cx = Children.map(children, child => {
    return cloneElement(child, { ...rest });
  });

  const { isLastPage, isSubmitting, page, previous } = rest;
  return (
    <Wrapper>
      <FormContainer title="Kaufabschluss">{cx}</FormContainer>
      <WizardControls>
        {page > 0 && (
          <ActionButton onClick={previous} direction="left">
            Zurück
          </ActionButton>
        )}
        {!isLastPage && (
          <ActionButton direction="right" type="submit">
            Weiter
          </ActionButton>
        )}
        {isLastPage && (
          <ActionButton type="submit" disabled={isSubmitting}>
            Weiter
          </ActionButton>
        )}
      </WizardControls>
      <Media query="(max-width: 52em)">
        {matches => (
          <Fragment>
            {matches && (
              <SummaryToggle
                onClick={event => {
                  event.preventDefault();
                  return toggleSummary();
                }}
              >
                Zusammenfassung ansehen
              </SummaryToggle>
            )}
          </Fragment>
        )}
      </Media>
    </Wrapper>
  );
};

// ---
// Step One
// ---
const StepOne = ({ values }) => {
  return (
    <Fragment>
      <InvoiceAddress />
      <Tip
        iconSize={30}
        text="Bitte beachten Sie, dass Ihre Rechnungsadresse aktuell nicht änderbar ist."
      />
      <Divider />
      <DeliveryAddress values={values} />
    </Fragment>
  );
};

const StepTwo = ({ values }) => (
  <Fragment>
    <Fieldset heading="Art des Kaufs">
      <Field
        name="order_information.order_type_invoice"
        render={({ field, form }) => (
          <Fieldset.Checkbox
            {...field}
            label="Bestellung auf Rechnung"
            onChange={e => {
              form.setFieldValue(
                'order_information.order_type_preview',
                !values.order_information.order_type_preview
              );
              field.onChange(e);
            }}
            checked={field.value}
          />
        )}
      />
      <Field
        name="order_information.order_type_preview"
        render={({ field, form }) => (
          <Fieldset.Checkbox
            {...field}
            label="Bestellung zur Ansicht"
            onChange={e => {
              form.setFieldValue(
                'order_information.order_type_invoice',
                !values.order_information.order_type_invoice
              );
              field.onChange(e);
            }}
            checked={field.value}
          />
        )}
      />
    </Fieldset>

    <Fieldset heading="Formscheiben">
      <Field
        name="frame_configuration.frame_formers.formers_required"
        render={({ field }) => (
          <Fieldset.Checkbox
            {...field}
            label="Formscheiben werden benötigt"
            checked={field.value}
          />
        )}
      />
    </Fieldset>

    <Fieldset heading="Komission">
      <Field
        name="order_information.order_commission.commission_required"
        render={({ field }) => (
          <Fieldset.Checkbox
            label="Komission wird benötigt"
            {...field}
            checked={field.value}
          />
        )}
      />
      <Field
        name="order_information.order_commission.commission_text"
        render={({ field }) => (
          <Fieldset.TextInput
            {...field}
            label="Ihre Komission"
            disabled={
              !values.order_information.order_commission.commission_required
            }
          />
        )}
      />
      <Error name="order_information.order_commission.commission_text" />
    </Fieldset>
    <Fieldset heading="E-Mail Adresse für Bestellbestätigung">
      <Field
        name="customer_email"
        render={({ field }) => (
          <Fieldset.TextInput {...field} label="E-Mail Adresse" />
        )}
      />
      <Error name="customer_email" />
    </Fieldset>
    <Fieldset heading="Notizen zur Bestellung">
      <Field
        name="order_information.order_comment"
        render={({ field }) => <Fieldset.TextArea {...field} />}
      />
    </Fieldset>
  </Fragment>
);

class OrderFormWizard extends Component {
  onSubmit = values => {
    const { submitWizard, history } = this.props;
    const orderForm = mapFormValuesToOrder(values);

    submitWizard({ values: orderForm, history });
  };

  render() {
    const { toggleOrderSummary } = this.props;

    const EnhancedCheckoutStep = ({ ...rest }) => (
      <CheckoutStep toggleSummary={toggleOrderSummary} {...rest} />
    );

    return (
      <FormWizard
        initialValues={{
          customer_invoice_address: {
            ...this.props.customer_invoice_address,
          },
          customer_delivery_address_id: null,
          customer_delivery_address_eq_invoice_address: true,
          customer_delivery_address: {
            ...this.props.customer_delivery_address.default,
          },
          customer_email: this.props.customer_email,
          order_information: {
            delivery_date: 'sofort',
            order_type_preview: false,
            order_type_invoice: true,
            order_comment: '',
            order_commission: {
              commission_required: false,
              commission_text: '',
            },
          },
          frame_configuration: {
            frame_formers: {
              formers_required: false,
            },
            frame_temples_imprint: {
              temples_imprint_required: false,
              temples_imprint_text: '',
            },
          },
        }}
        onSubmit={this.onSubmit}
      >
        <FormWizard.FormWizardPage
          step={1}
          validate={values => {
            const form = {
              customer_invoice_address: values.customer_invoice_address,
              customer_delivery_address_eq_invoice_address:
                values.customer_delivery_address_eq_invoice_address,
              customer_delivery_address: values.customer_delivery_address,
              customer_delivery_address_id: values.customer_delivery_address_id,
            };

            return validateYupSchema(form, StepOneSchema);
          }}
        >
          <EnhancedCheckoutStep>
            <StepOne />
          </EnhancedCheckoutStep>
        </FormWizard.FormWizardPage>
        <FormWizard.FormWizardPage
          step={2}
          validate={values => {
            const form = {
              customer_email: values.customer_email,
              frame_configuration: values.frame_configuration,
              order_information: values.order_information,
            };
            return validateYupSchema(form, StepTwoSchema);
          }}
        >
          <EnhancedCheckoutStep>
            <StepTwo />
          </EnhancedCheckoutStep>
        </FormWizard.FormWizardPage>
      </FormWizard>
    );
  }
}

const redirectIfCartIsEmpty = cartIsEmpty =>
  branch(cartIsEmpty, renderComponent(() => <Redirect to="/" />));

const renderNothingWhileLoading = isLoading => branch(isLoading, renderNothing);

const enhance = compose(
  redirectIfCartIsEmpty(props => props.cartIsEmpty),
  renderNothingWhileLoading(props => !props.customer_invoice_address)
);

export default enhance(OrderFormWizard);



// WEBPACK FOOTER //
// ../shop_main/components/orderformwizard/index.js